import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDate } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Plus, 
  Edit, 
  Trash2,
  Loader2, 
  Phone,
  CheckCircle,
  XCircle,
  AlertCircle,
  MapPin,
  Webhook,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { WebhookHealthPanel } from '../components/WebhookHealthPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
];

const OutboundNumbersPage = () => {
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingNumber, setEditingNumber] = useState(null);
  const [deletingNumber, setDeletingNumber] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Webhook state
  const [webhooks, setWebhooks] = useState([]);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [creatingWebhook, setCreatingWebhook] = useState(false);
  
  const [formData, setFormData] = useState({
    phone_number: '',
    label: '',
    state: '',
    office: '',
    is_default: false,
    is_active: true
  });

  useEffect(() => {
    fetchNumbers();
    fetchWebhooks();
  }, []);

  const fetchNumbers = async () => {
    try {
      const response = await axios.get(`${API}/admin/outbound-numbers`);
      setNumbers(response.data.outbound_numbers || []);
    } catch (error) {
      toast.error('Failed to load outbound numbers');
    } finally {
      setLoading(false);
    }
  };

  const fetchWebhooks = async () => {
    try {
      setWebhookLoading(true);
      const response = await axios.get(`${API}/ringcentral/webhooks`);
      if (response.data.success) {
        setWebhooks(response.data.subscriptions || []);
      }
    } catch (error) {
      console.error('Failed to load webhooks:', error);
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleCreateSmsWebhook = async () => {
    try {
      setCreatingWebhook(true);
      const webhookUrl = `${window.location.origin}/api/ringcentral/webhook`;
      const response = await axios.post(`${API}/ringcentral/webhooks/sms`, {
        webhook_url: webhookUrl
      });
      
      if (response.data.success) {
        toast.success('SMS webhook created successfully! Incoming messages will now be received.');
        fetchWebhooks();
      } else {
        toast.error(response.data.error || 'Failed to create webhook');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || 'Failed to create webhook';
      toast.error(errorMsg);
    } finally {
      setCreatingWebhook(false);
    }
  };

  const handleDeleteWebhook = async (subscriptionId) => {
    try {
      const response = await axios.delete(`${API}/ringcentral/webhooks/${subscriptionId}`);
      if (response.data.success) {
        toast.success('Webhook deleted');
        fetchWebhooks();
      } else {
        toast.error(response.data.error || 'Failed to delete webhook');
      }
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const handleSyncRingCentral = async () => {
    try {
      setSyncing(true);
      const response = await axios.post(`${API}/admin/outbound-numbers/sync-ringcentral`);
      toast.success(response.data.message);
      fetchNumbers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync from RingCentral');
    } finally {
      setSyncing(false);
    }
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phone_number: formatted });
  };

  const handleOpenDialog = (number = null) => {
    if (number) {
      setEditingNumber(number);
      setFormData({
        phone_number: number.phone_number,
        label: number.label || '',
        state: number.state || '',
        office: number.office || '',
        is_default: number.is_default || false,
        is_active: number.is_active !== false
      });
    } else {
      setEditingNumber(null);
      setFormData({
        phone_number: '',
        label: '',
        state: '',
        office: '',
        is_default: false,
        is_active: true
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number has 10 digits
    const digits = formData.phone_number.replace(/\D/g, '');
    if (digits.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    
    setSaving(true);

    try {
      const payload = {
        ...formData,
        phone_number: formData.phone_number // Keep formatted
      };
      
      if (editingNumber) {
        await axios.put(`${API}/admin/outbound-numbers/${editingNumber.id}`, payload);
        toast.success('Outbound number updated successfully');
      } else {
        await axios.post(`${API}/admin/outbound-numbers`, payload);
        toast.success('Outbound number added successfully');
      }
      setShowDialog(false);
      fetchNumbers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save outbound number');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingNumber) return;
    setSaving(true);

    try {
      await axios.delete(`${API}/admin/outbound-numbers/${deletingNumber.id}`);
      toast.success('Outbound number deleted successfully');
      setShowDeleteDialog(false);
      setDeletingNumber(null);
      fetchNumbers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete outbound number');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (number) => {
    try {
      await axios.put(`${API}/admin/outbound-numbers/${number.id}`, {
        is_active: !number.is_active
      });
      toast.success(`Number ${!number.is_active ? 'activated' : 'deactivated'}`);
      fetchNumbers();
    } catch (error) {
      toast.error('Failed to update number');
    }
  };

  const handleSetDefault = async (number) => {
    try {
      await axios.put(`${API}/admin/outbound-numbers/${number.id}`, {
        is_default: true
      });
      toast.success('Default number updated');
      fetchNumbers();
    } catch (error) {
      toast.error('Failed to set default');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="outbound-numbers-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="outbound-numbers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Outbound Phone Numbers</h1>
          <p className="text-gray-500">Configure RingCentral outbound numbers for SMS and calls</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleSyncRingCentral} 
            disabled={syncing}
            data-testid="sync-ringcentral-btn"
          >
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
            Sync from RingCentral
          </Button>
          <Button onClick={() => handleOpenDialog()} data-testid="add-outbound-number-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Number
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Outbound Numbers ({numbers.length})
          </CardTitle>
          <CardDescription>
            Authorized phone numbers for outbound SMS and voice calls
          </CardDescription>
        </CardHeader>
        <CardContent>
          {numbers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Phone className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No outbound numbers configured</p>
              <p className="text-sm">Click &quot;Add Number&quot; to configure one</p>
            </div>
          ) : (
            <div className="divide-y">
              {numbers.map((number) => (
                <div 
                  key={number.id} 
                  className="py-4 flex items-center justify-between"
                  data-testid={`outbound-number-row-${number.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 font-mono">{number.phone_number}</span>
                      {number.label && (
                        <Badge variant="outline">{number.label}</Badge>
                      )}
                      {number.is_default && (
                        <Badge className="bg-blue-600">Default</Badge>
                      )}
                      <Badge variant={number.is_active ? "default" : "secondary"}>
                        {number.is_active ? (
                          <><CheckCircle className="w-3 h-3 mr-1" />Active</>
                        ) : (
                          <><XCircle className="w-3 h-3 mr-1" />Inactive</>
                        )}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      {number.state && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {US_STATES.find(s => s.code === number.state)?.name || number.state}
                        </span>
                      )}
                      {number.office && (
                        <span>Office: {number.office}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!number.is_default && number.is_active && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleSetDefault(number)}
                        data-testid={`set-default-${number.id}`}
                      >
                        Set Default
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleToggleActive(number)}
                      data-testid={`toggle-outbound-${number.id}`}
                    >
                      {number.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleOpenDialog(number)}
                      data-testid={`edit-outbound-${number.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => {
                        setDeletingNumber(number);
                        setShowDeleteDialog(true);
                      }}
                      data-testid={`delete-outbound-${number.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* SMS Webhook Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="w-5 h-5" />
            SMS Webhook Configuration
          </CardTitle>
          <CardDescription>
            Configure webhook to receive incoming SMS messages from participants
          </CardDescription>
        </CardHeader>
        <CardContent>
          <WebhookHealthPanel onRenewed={fetchWebhooks} />
          {webhookLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : webhooks.length === 0 ? (
            <div className="text-center py-6">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
              <p className="font-medium text-gray-900">No SMS Webhook Configured</p>
              <p className="text-sm text-gray-500 mb-4">
                Without a webhook, incoming SMS replies from participants won&apos;t appear in the messenger.
              </p>
              <Button 
                onClick={handleCreateSmsWebhook} 
                disabled={creatingWebhook}
                data-testid="setup-sms-webhook-btn"
              >
                {creatingWebhook ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Webhook className="w-4 h-4 mr-2" />
                )}
                Setup SMS Webhook
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {webhooks.map((webhook) => (
                <div 
                  key={webhook.id} 
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${webhook.status === 'Active' ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <div>
                      <p className="font-medium text-sm">SMS Webhook</p>
                      <p className="text-xs text-gray-500">
                        Status: {webhook.status} | Expires: {webhook.expirationTime ? formatDate(webhook.expirationTime) : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={webhook.status === 'Active' ? 'default' : 'secondary'}>
                      {webhook.status}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteWebhook(webhook.id)}
                      title="Delete webhook"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={fetchWebhooks}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Status
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingNumber ? 'Edit Outbound Number' : 'Add Outbound Number'}</DialogTitle>
            <DialogDescription>
              Configure an authorized outbound phone number for RingCentral
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                value={formData.phone_number}
                onChange={handlePhoneChange}
                placeholder="(615) 750-2145"
                required
                data-testid="outbound-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="label">Label</Label>
              <Input
                id="label"
                value={formData.label}
                onChange={(e) => setFormData({...formData, label: e.target.value})}
                placeholder="e.g., Tennessee Outbound"
                data-testid="outbound-label-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Select
                  value={formData.state || "none"}
                  onValueChange={(value) => setFormData({...formData, state: value === "none" ? "" : value})}
                >
                  <SelectTrigger data-testid="outbound-state-select">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {US_STATES.map((state) => (
                      <SelectItem key={state.code} value={state.code}>{state.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="office">Office</Label>
                <Input
                  id="office"
                  value={formData.office}
                  onChange={(e) => setFormData({...formData, office: e.target.value})}
                  placeholder="e.g., Nashville"
                  data-testid="outbound-office-input"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_default}
                  onCheckedChange={(checked) => setFormData({...formData, is_default: checked})}
                  data-testid="outbound-default-switch"
                />
                <Label>Set as default</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  data-testid="outbound-active-switch"
                />
                <Label>Active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-outbound-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingNumber ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Delete Outbound Number
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {deletingNumber?.phone_number}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={saving}
              data-testid="confirm-delete-outbound-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OutboundNumbersPage;
